import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { z } from "zod";
import { toast } from "sonner";
import { getPaymentSettingsByYear } from "../../../api/paiement_settings";
import { getBankAccountsByPaymentSettingId } from "../../../api/bank_account";
import { createPayment } from "../../../api/studentPaiement";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getStudentDiscountByYear } from "../../../api/studentPaiement";
import { Select } from "antd";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  show: boolean;
  onClose: () => void;
  students: any[];
  onSaved?: () => void;
  currency?: string;
};

enum PaymentType {
  ESPECE = "Espèce",
  CHEQUE = "Chèque",
  VIREMENT = "Virement",
}

const schema = z.object({
  amountReceived: z.number({ invalid_type_error: "Montant requis" }).positive(),
  refId: z.string().nonempty("Choisissez un compte bancaire valide"),
  type: z.enum([PaymentType.ESPECE, PaymentType.CHEQUE, PaymentType.VIREMENT]),
  receptionDate: z.string().nonempty("Date de reception requise"),
  description: z.string().optional(),
  allocations: z
    .array(
      z.object({
        planId: z.string(),
        amount: z.number().min(0),
      }),
    )
    .min(1, "Selectionnez au moins un plan"),
});

const CreatePaymentModal = ({
  show,
  onClose,
  students,
  onSaved,
  currency = "FCFA",
}: Props) => {
  const getTodayInputValue = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const { selectedYear } = useAcademicYear();

  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [amountReceived, setAmountReceived] = useState<number>(0);
  const [refId, setRefId] = useState<string>("");
  const [paymentType, setPaymentType] = useState<PaymentType>(
    PaymentType.ESPECE,
  );
  const [receptionDate, setReceptionDate] =
    useState<string>(getTodayInputValue());
  const [description, setDescription] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allocations, setAllocations] = useState<
    { planId: string; amount: number }[]
  >([]);

  const [paymentSettings, setPaymentSettings] = useState<any>();
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [discountMaps, setDiscountMaps] = useState<{
    byStudentPlanId: Record<string, number>;
    byPaymentPlanId: Record<string, number>;
  }>({ byStudentPlanId: {}, byPaymentPlanId: {} });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedStudent = useMemo(
    () => students.find((s: any) => s.id === selectedStudentId),
    [students, selectedStudentId],
  );

  const getPlanAmount = (plan: any) =>
    Number(plan?.PaymentPlan?.amount ?? plan?.amount ?? 0);

  const getPlanTemplateId = (plan: any) =>
    String(plan?.id_paymentPlans || plan?.PaymentPlan?.id || "");

  const getDiscountAmountByPlan = (plan: any) => {
    const studentPlanId = String(plan?.id || "");
    const templatePlanId = getPlanTemplateId(plan);
    const byStudent = Number(discountMaps.byStudentPlanId[studentPlanId] || 0);
    const byTemplate = Number(
      discountMaps.byPaymentPlanId[templatePlanId] || 0,
    );
    return byStudent > 0 ? byStudent : byTemplate;
  };

  const getRemainingByPlan = (plan: any) => {
    const gross = getPlanAmount(plan);
    const discounted = getDiscountAmountByPlan(plan);
    const netToPay = Math.max(0, gross - discounted);
    return Math.max(0, netToPay - Number(plan?.paid || 0));
  };

  const validPlans = useMemo(() => {
    const plans = Array.isArray(selectedStudent?.plan)
      ? selectedStudent.plan
      : [];

    const myPayments: any[] = Array.isArray(selectedStudent?.payments)
      ? selectedStudent?.payments
      : [];
    const myPaymentRepartitions: any[] = myPayments
      .map((payment) =>
        Array.isArray(payment?.repartitions) ? payment?.repartitions : [],
      )
      .flat();

    const myPlansWithPaid = plans.map((plan: any) => ({
      ...plan,
      paid: myPaymentRepartitions.reduce(
        (s, r) => s + (r.payment_plan?.id === plan.id ? r.amount : 0),
        0,
      ),
    }));
    return myPlansWithPaid.filter((p: any) => getRemainingByPlan(p) > 0);
  }, [selectedStudent, discountMaps]);

  const fetchStudentDiscounts = async () => {
    try {
      if (!selectedYear?.id || !selectedStudentId) {
        setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
        return;
      }

      const res = await getStudentDiscountByYear(
        selectedStudentId,
        selectedYear.id,
        buildPermissionContext(Resource.PAYMENT, Action.CREATE),
      );
      const byStudentPlanId: Record<string, number> = {};
      const byPaymentPlanId: Record<string, number> = {};

      const discounts = res?.data?.discount || [];
      discounts.forEach((discount: any) => {
        (discount?.RepartitionStudentDiscounts || []).forEach((rep: any) => {
          const repAmount = Number(rep?.amount || 0);
          const studentPlanId = String(rep?.id_studentPayementPlans || "");
          const paymentPlanId = String(
            rep?.StudentPayementPlan?.PaymentPlan?.id || "",
          );

          if (studentPlanId) {
            byStudentPlanId[studentPlanId] =
              Number(byStudentPlanId[studentPlanId] || 0) + repAmount;
          }
          if (paymentPlanId) {
            byPaymentPlanId[paymentPlanId] =
              Number(byPaymentPlanId[paymentPlanId] || 0) + repAmount;
          }
        });
      });

      setDiscountMaps({ byStudentPlanId, byPaymentPlanId });
    } catch (error: any) {
      setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Impossible de charger les bourses/remises de l'etudiant",
      );
    }
  };

  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear?.id) return;
      const res = await getPaymentSettingsByYear(
        selectedYear.id,
        buildPermissionContext(Resource.PAYMENT, Action.CREATE),
      );
      setPaymentSettings(res?.data?.[0]);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite, reessayez plus tard",
      );
    }
  };

  const fetchBankAccountsByPaymentSettingId = async (
    id_paymentSetting: any,
  ) => {
    try {
      if (!id_paymentSetting) return;
      const res = await getBankAccountsByPaymentSettingId(
        id_paymentSetting,
        buildPermissionContext(Resource.PAYMENT, Action.CREATE),
      );
      setBankAccounts(res?.data || []);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite, reessayez plus tard",
      );
    }
  };

  useEffect(() => {
    fetchPaymentParameters();
  }, [selectedYear?.id]);

  useEffect(() => {
    if (!paymentSettings?.id) return;
    fetchBankAccountsByPaymentSettingId(paymentSettings.id);
  }, [paymentSettings?.id]);

  useEffect(() => {
    if (!show) return;
    const firstStudentId = students?.[0]?.id || "";
    setSelectedStudentId(firstStudentId);
    setSelectedPlanIds([]);
    setAllocations([]);
    setAmountReceived(0);
    setReceptionDate(getTodayInputValue());
    setDescription("");
    setPaymentType(PaymentType.ESPECE);
    setErrors({});
  }, [show, students]);

  useEffect(() => {
    if (!show) return;
    if (!bankAccounts.length) {
      setRefId("");
      return;
    }
    setRefId(bankAccounts[0].id);
  }, [bankAccounts, show]);

  useEffect(() => {
    setSelectedPlanIds([]);
    setAllocations([]);
  }, [selectedStudentId]);

  useEffect(() => {
    if (!show) return;
    fetchStudentDiscounts();
  }, [show, selectedYear?.id, selectedStudentId]);

  useEffect(() => {
    const next = selectedPlanIds
      .map((id) => {
        const plan = validPlans.find((p: any) => p.id === id);
        if (!plan) return null;
        const existing = allocations.find((a) => a.planId === id)?.amount || 0;
        const remain = getRemainingByPlan(plan);
        return { planId: id, amount: Math.min(existing, remain) };
      })
      .filter(Boolean) as { planId: string; amount: number }[];

    setAllocations(next);
  }, [selectedPlanIds, validPlans]);

  const totalAllocated = useMemo(
    () => allocations.reduce((sum, a) => sum + (a.amount || 0), 0),
    [allocations],
  );

  const remaining = useMemo(
    () => Math.max(0, amountReceived - totalAllocated),
    [amountReceived, totalAllocated],
  );

  const togglePlan = (planId: string) => {
    setSelectedPlanIds((prev) =>
      prev.includes(planId)
        ? prev.filter((id) => id !== planId)
        : [...prev, planId],
    );
  };

  const setAllocation = (planId: string, value: number) => {
    const plan = validPlans.find((p: any) => p.id === planId);
    if (!plan) return;
    const remain = getRemainingByPlan(plan);
    const amount = Math.max(0, Math.min(value, remain));
    setAllocations((prev) => {
      const idx = prev.findIndex((a) => a.planId === planId);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { planId, amount };
        return copy;
      }
      return [...prev, { planId, amount }];
    });
  };

  const autoDistribute = () => {
    let rest = amountReceived;
    const sorted = [...selectedPlanIds]
      .map((id) => validPlans.find((p: any) => p.id === id))
      .filter(Boolean)
      .sort(
        (a: any, b: any) =>
          new Date(a?.PaymentPlan?.due_date).getTime() -
          new Date(b?.PaymentPlan?.due_date).getTime(),
      );
    const next: { planId: string; amount: number }[] = [];

    for (const plan of sorted) {
      const remain = getRemainingByPlan(plan);
      const assign = Math.min(remain, rest);
      next.push({ planId: plan.id, amount: assign });
      rest -= assign;
      if (rest <= 0) break;
    }
    setAllocations(next);
  };

  const handleSubmit = async () => {
    const payloadForValidation = {
      amountReceived,
      refId,
      type: paymentType,
      receptionDate,
      description: description || undefined,
      allocations,
    };

    const parse = schema.safeParse(payloadForValidation);
    const nextErrors: Record<string, string> = {};
    if (!selectedStudentId) {
      nextErrors.student = "Selectionnez un etudiant";
    }

    if (!parse.success) {
      parse.error.issues.forEach((issue) => {
        nextErrors[issue.path.join(".")] = issue.message;
      });
    }

    const planLimitsOk = allocations.every((allocation) => {
      const plan = validPlans.find((p: any) => p.id === allocation.planId);
      if (!plan) return false;
      return allocation.amount <= getRemainingByPlan(plan);
    });

    if (!planLimitsOk) {
      nextErrors.allocations = "Montant superieur au restant net d'un plan";
    }

    if (totalAllocated > amountReceived) {
      nextErrors.total = "La somme repartie depasse le montant percu";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const payload = {
        payments: [
          {
            id_studentProfil: selectedStudentId,
            id_bankAccount: refId,
            type: paymentType,
            description,
            amount_total: amountReceived,
            reception_date: receptionDate
              ? new Date(receptionDate)
              : new Date(),
            selectedPlans: allocations.map((a) => ({
              id: a.planId,
              amount: a.amount,
            })),
          },
        ],
      };

      await createPayment(payload);
      toast.success("Paiement enregistre avec succes");
      if (onSaved) onSaved();
      onClose();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produite, reessayez plus tard",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("create_payment_modal");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };
  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title>Nouveau Paiement</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white" id="create_payment_modal">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Etudiant</label>
            <Select
              showSearch
              placeholder="Sélectionner un étudiant"
              optionFilterProp="children"
              getPopupContainer={getModalContainer}
              filterOption={(input, option) =>
                option?.label
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) ||
                false?.toString().toLowerCase().includes(input.toLowerCase())
              }
              className="w-100"
              value={selectedStudentId}
              onChange={(val) => {
                setSelectedStudentId(val as string);
              }}
              options={students.map((student: any) => ({
                value: student.id,
                label: `${student.classe || "-"} | (${student.utilisateur})`,
              }))}
              notFoundContent={
                students.length === 0 ? "Aucun étudiant disponible" : undefined
              }
            />
            {errors.student && (
              <div className="text-danger small mt-1">{errors.student}</div>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">Montant percu</label>
            <input
              type="number"
              className="form-control"
              min={0}
              value={amountReceived}
              onChange={(e) => setAmountReceived(Number(e.target.value || 0))}
            />
            {errors.amountReceived && (
              <div className="text-danger small mt-1">
                {errors.amountReceived}
              </div>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">Type</label>
            <select
              className="form-select"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as PaymentType)}
            >
              {Object.values(PaymentType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Compte bancaire</label>
            <select
              className="form-select"
              value={refId}
              onChange={(e) => setRefId(e.target.value)}
              disabled={!bankAccounts.length}
            >
              {!bankAccounts.length && (
                <option value="">Aucun compte bancaire disponible</option>
              )}
              {bankAccounts.map((account: any) => (
                <option key={account.id} value={account.id}>
                  {account.bank_name} - {account.account_title} (
                  {account.account_number})
                </option>
              ))}
            </select>
            {errors.refId && (
              <div className="text-danger small mt-1">{errors.refId}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Description (optionnelle)</label>
            <input
              type="text"
              className="form-control"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Date de reception</label>
            <input
              type="date"
              className="form-control"
              value={receptionDate}
              onChange={(e) => setReceptionDate(e.target.value)}
            />
            {errors.receptionDate && (
              <div className="text-danger small mt-1">
                {errors.receptionDate}
              </div>
            )}
          </div>
        </div>

        <div className="card shadow-sm border-dark rounded mt-3">
          <div className="card-header bg-light d-flex align-items-center justify-content-between">
            <h6 className="mb-0">Plans de paiement de l'etudiant</h6>
            <button className="btn btn-info btn-sm" onClick={autoDistribute}>
              Auto-repartition
            </button>
          </div>
          <div className="card-body">
            {validPlans.length === 0 ? (
              <span className="text-muted">
                Aucun plan disponible pour cet etudiant.
              </span>
            ) : (
              <>
                <div className="mb-3">
                  <div className="dropdown">
                    <button
                      className="btn btn-outline-light bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                      style={{ minWidth: "260px" }}
                    >
                      <i className="ti ti-wallet me-2" />
                      {selectedPlanIds.length > 0
                        ? `${selectedPlanIds.length} plan(s) selectionne(s)`
                        : "Plans de paiement"}
                    </button>

                    <ul
                      className="dropdown-menu p-2"
                      onClick={(e) => e.stopPropagation()}
                      style={{ minWidth: "300px" }}
                    >
                      {validPlans.map((plan: any) => {
                        const remain = getRemainingByPlan(plan);
                        return (
                          <li key={plan.id} className="mb-2">
                            <label
                              className={`dropdown-item d-flex flex-column ${
                                selectedPlanIds.includes(plan.id)
                                  ? "bg-light text-primary rounded-1"
                                  : ""
                              }`}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input m-0"
                                  checked={selectedPlanIds.includes(plan.id)}
                                  onChange={() => togglePlan(plan.id)}
                                />
                                <span>{plan?.PaymentPlan?.label}</span>
                              </div>
                              <div className="ms-4">
                                <small className="text-muted">
                                  Reste: {remain.toLocaleString("fr-FR")}{" "}
                                  {currency}
                                </small>
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {selectedPlanIds.length > 0 && (
                  <div className="d-flex flex-column gap-2">
                    {selectedPlanIds.map((planId) => {
                      const plan = validPlans.find((p: any) => p.id === planId);
                      if (!plan) return null;
                      const remain = getRemainingByPlan(plan);
                      const alloc =
                        allocations.find((a) => a.planId === planId)?.amount ||
                        0;
                      return (
                        <div
                          key={planId}
                          className="d-flex align-items-center justify-content-between border rounded p-2"
                        >
                          <div className="ms-4">
                            <span>{plan?.PaymentPlan?.label}</span>
                            <br />
                            <small className="text-muted">
                              Reste: {remain.toLocaleString("fr-FR")} {currency}
                            </small>
                          </div>
                          <div className="input-group" style={{ width: 220 }}>
                            <input
                              type="number"
                              className="form-control"
                              min={0}
                              max={remain}
                              value={alloc}
                              onChange={(e) =>
                                setAllocation(
                                  planId,
                                  Number(e.target.value || 0),
                                )
                              }
                            />
                            <span className="input-group-text">{currency}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {errors.allocations && (
              <div className="text-danger small mt-2">{errors.allocations}</div>
            )}
            {errors.total && (
              <div className="text-danger small mt-2">{errors.total}</div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-3">
          <div className="fw-semibold">
            Reste: {remaining.toLocaleString("fr-FR")} {currency}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light me-1" onClick={onClose}>
              Annuler
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Validation..." : "Valider"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default withPermission(
  CreatePaymentModal,
  [buildPermission(Resource.PAYMENT, Action.CREATE)],
  "masquer",
);
